import { Tooltip } from '@/components/ui/tooltip';
import { Badge, Box } from '@chakra-ui/react';
import { BriefDiffusionStatus } from '@fluenceur/types';
import { Archive, Sparks, WarningTriangle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { formatDiffusionDate } from './brief-diffusion';

/*
 * Every brief badge is the design system's Badge (`badge.recipe.ts`: subtle
 * palette background, contrasted palette text). Only the palette is chosen
 * here, never a hand-picked colour, so the briefs read like the rest of the app.
 */

/**
 * Marks a UGC brief. Teal rather than lilac, which the theme keeps for what the
 * AI wrote; the type itself never changes after creation.
 */
export const UgcTypeBadge = () => {
  const { t } = useTranslation();
  return <Badge colorPalette="teal">{t('collabs.briefTemplates.editor.ugcTag')}</Badge>;
};

/** Small "AI draft" badge for a brief offered in the proposal wizard. */
export const AiDraftBadge = () => {
  const { t } = useTranslation();
  return (
    <Badge colorPalette="lilac">
      <Sparks width={11} height={11} />
      {t('collabs.briefTemplates.aiDraftBadge')}
    </Badge>
  );
};

/** "Archived" state badge for library rows. */
export const ArchivedBadge = () => {
  const { t } = useTranslation();
  return (
    <Badge colorPalette="gray">
      <Archive width={11} height={11} />
      {t('collabs.briefTemplates.archivedBadge')}
    </Badge>
  );
};

/** Marks a template that can't be sent as-is (missing deliverables, etc.). */
export const IncompleteBadge = () => {
  const { t } = useTranslation();
  return (
    <Badge colorPalette="yellow">
      <WarningTriangle width={11} height={11} />
      {t('collabs.briefTemplates.incompleteBadge')}
    </Badge>
  );
};

const DIFFUSION_KEY = 'collabs.briefTemplates.diffusion';

const DIFFUSION_BADGE: Record<BriefDiffusionStatus, { key: string; palette: string }> = {
  [BriefDiffusionStatus.OPEN]: { key: 'open', palette: 'green' },
  [BriefDiffusionStatus.STOPPED]: { key: 'stopped', palette: 'gray' },
  [BriefDiffusionStatus.OFF]: { key: 'direct', palette: 'gray' },
};

interface DiffusionBadgeProps {
  status: BriefDiffusionStatus;
  /** When the brief was opened / stopped; the hover text names the day when given. */
  openedAt?: string | null;
  stoppedAt?: string | null;
}

/**
 * How a brief reaches creators: distributed to matching creators (green),
 * stopped, or sent by hand only (both neutral). Hovering says what that means
 * and since when.
 */
export const DiffusionBadge = ({
  status,
  openedAt = null,
  stoppedAt = null,
}: DiffusionBadgeProps) => {
  const { t, i18n } = useTranslation();
  const { key, palette } = DIFFUSION_BADGE[status];
  const day =
    status === BriefDiffusionStatus.OPEN
      ? formatDiffusionDate(openedAt, i18n.language)
      : status === BriefDiffusionStatus.STOPPED
        ? formatDiffusionDate(stoppedAt, i18n.language)
        : '';
  const hint = day
    ? t(`${DIFFUSION_KEY}.badgeHint.${key}Since`, { date: day })
    : t(`${DIFFUSION_KEY}.badgeHint.${key}`);

  return (
    <Tooltip content={hint} showArrow portalled positioning={{ placement: 'top' }}>
      <Badge colorPalette={palette} cursor="default" tabIndex={0}>
        {t(`${DIFFUSION_KEY}.badge.${key}`)}
      </Badge>
    </Tooltip>
  );
};

/**
 * Editor header pill for a brief that was opened once: open (green dot) or
 * stopped (grey dot). A brief never opened shows nothing.
 */
export const DiffusionStatusPill = ({ status }: { status: BriefDiffusionStatus }) => {
  const { t } = useTranslation();
  if (status === BriefDiffusionStatus.OFF) return null;
  const isOpen = status === BriefDiffusionStatus.OPEN;
  return (
    <Badge colorPalette={isOpen ? 'green' : 'gray'} size="md" flex="none">
      <Box boxSize="6px" rounded="full" bg="colorPalette.solid" flex="none" />
      {t(isOpen ? `${DIFFUSION_KEY}.openTitle` : `${DIFFUSION_KEY}.stoppedTitle`)}
    </Badge>
  );
};
